import { Box, Divider, Paper, Stack, Typography, useTheme } from "@mui/material";
import {
  ActivityStatus,
  ActivityType,
  ExecutionPlanType,
  WorkflowOverviewStatsQuery,
} from "generated-graphql/graphql";
import { useOmnisearchDatagrid } from "hooks/useOmnisearchDatagrid";
import { KeyMetric } from "./KeyMetric";
import { AssignmentManagementTableFilterKeys } from "../AssignmentManagerTable/useAssignmentManagerOmnisearch";

export function WorkflowOverviewKeyMetrics({
  data,
  loading,
}: {
  data?: WorkflowOverviewStatsQuery["workflowOverviewSeasonSummary"];
  loading: boolean;
}) {
  const theme = useTheme();
  // Use same pattern as Summary/KeyMetrics - createUrlFromOmnisearchKeyValues with href
  const { createUrlFromOmnisearchKeyValues } = useOmnisearchDatagrid({
    persistenceKey: "workflow-overview-key-metrics",
  });

  // In Error metrics
  const inErrorHref = createUrlFromOmnisearchKeyValues({
    keyValues: [
      [AssignmentManagementTableFilterKeys.ActivityStatus, ActivityStatus.Error],
    ],
    keysToKeep: ["reportingYear", "reportKind"],
  });

  const inErrorUntaggedHref = createUrlFromOmnisearchKeyValues({
    keyValues: [
      [AssignmentManagementTableFilterKeys.ActivityStatus, ActivityStatus.Error],
      [AssignmentManagementTableFilterKeys.Tags, "null"],
    ],
    keysToKeep: ["reportingYear", "reportKind"],
  });

  const inErrorComplianceHref = createUrlFromOmnisearchKeyValues({
    keyValues: [
      [AssignmentManagementTableFilterKeys.ActivityStatus, ActivityStatus.Error],
      [AssignmentManagementTableFilterKeys.Tags, "compliance"],
    ],
    keysToKeep: ["reportingYear", "reportKind"],
  });

  const inErrorEngineeringHref = createUrlFromOmnisearchKeyValues({
    keyValues: [
      [AssignmentManagementTableFilterKeys.ActivityStatus, ActivityStatus.Error],
      [AssignmentManagementTableFilterKeys.Tags, "engineering"],
    ],
    keysToKeep: ["reportingYear", "reportKind"],
  });

  // Wait for All
  const waitForAllHref = createUrlFromOmnisearchKeyValues({
    keyValues: [
      [
        AssignmentManagementTableFilterKeys.ActivityType,
        `${ActivityType.WaitForAllStateReports}|${ActivityType.WaitForAllCredentialReports}|${ActivityType.WaitForAllCnReports}|${ActivityType.WaitForAllAltIdReports}|${ActivityType.WaitForAllLepc}|${ActivityType.WaitForAllFd}`,
      ],
    ],
    keysToKeep: ["reportingYear", "reportKind"],
  });

  // In Manual metrics
  const inManualHref = createUrlFromOmnisearchKeyValues({
    keyValues: [
      [AssignmentManagementTableFilterKeys.ExecutionPlan, ExecutionPlanType.Human],
    ],
    keysToKeep: ["reportingYear", "reportKind"],
  });

  const inManualUnassignedHref = createUrlFromOmnisearchKeyValues({
    keyValues: [
      [AssignmentManagementTableFilterKeys.ExecutionPlan, ExecutionPlanType.Human],
      [AssignmentManagementTableFilterKeys.Assignee, "Unassigned"],
    ],
    keysToKeep: ["reportingYear", "reportKind"],
  });

  // In Progress
  const inProgressHref = createUrlFromOmnisearchKeyValues({
    keyValues: [
      [AssignmentManagementTableFilterKeys.ActivityStatus, ActivityStatus.InProgress],
    ],
    keysToKeep: ["reportingYear", "reportKind"],
  });

  // In Automation (not error) - ExecutionPlan = AUTO, ActivityStatus = InProgress
  const inAutomationHref = createUrlFromOmnisearchKeyValues({
    keyValues: [
      [AssignmentManagementTableFilterKeys.ExecutionPlan, ExecutionPlanType.Automation],
      [AssignmentManagementTableFilterKeys.ActivityStatus, ActivityStatus.InProgress],
    ],
    keysToKeep: ["reportingYear", "reportKind"],
  });

  // In Geppetto, in flight - ActivityType = Geppetto, ActivityStatus = InProgress
  const geppettoInFlightHref = createUrlFromOmnisearchKeyValues({
    keyValues: [
      [AssignmentManagementTableFilterKeys.ActivityType, ActivityType.Geppetto],
      [AssignmentManagementTableFilterKeys.ActivityStatus, ActivityStatus.InProgress],
    ],
    keysToKeep: ["reportingYear", "reportKind"],
  });

  // In Geppetto, in error - ActivityType = Geppetto, ActivityStatus = Error
  const geppettoInErrorHref = createUrlFromOmnisearchKeyValues({
    keyValues: [
      [AssignmentManagementTableFilterKeys.ActivityType, ActivityType.Geppetto],
      [AssignmentManagementTableFilterKeys.ActivityStatus, ActivityStatus.Error],
    ],
    keysToKeep: ["reportingYear", "reportKind"],
  });

  // In Automation, in error - ExecutionPlan = AUTO, ActivityStatus = Error
  const automationInErrorHref = createUrlFromOmnisearchKeyValues({
    keyValues: [
      [AssignmentManagementTableFilterKeys.ExecutionPlan, ExecutionPlanType.Automation],
      [AssignmentManagementTableFilterKeys.ActivityStatus, ActivityStatus.Error],
    ],
    keysToKeep: ["reportingYear", "reportKind"],
  });

  return (
    <Paper
      sx={{
        height: "100%",
        width: "100%",
        py: theme.spacing(2.5),
        px: theme.spacing(3),
        overflow: "hidden",
      }}
      elevation={0}
    >
      <Stack direction="column" spacing={2.5} height="100%">
        <Typography variant="body1" color={theme.palette.text.secondary} fontWeight={500}>
          Key Metrics
        </Typography>

        <Stack 
          direction={{ xs: "column", lg: "row" }}
          spacing={3}
          flex={1}
          sx={{ minWidth: 0 }}
        >
          {/* Error Metrics Section */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="caption"
              color="error.main"
              sx={{ 
                mb: 1.5, 
                display: "block", 
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                fontSize: "0.7rem",
              }}
            >
              Errors
            </Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                gap: 1.5,
              }}
            >
              <KeyMetric
                name="Total"
                value={data?.workflowsCountError}
                loading={loading}
                href={inErrorHref}
              />
              <KeyMetric
                name="Untagged"
                value={data?.workflowsCountErrorUntagged}
                loading={loading}
                href={inErrorUntaggedHref}
              />
              <KeyMetric
                name="Compliance"
                value={data?.workflowsCountErrorCompliance}
                loading={loading}
                href={inErrorComplianceHref}
              />
              <KeyMetric
                name="Engineering"
                value={data?.workflowsCountErrorEngineering}
                loading={loading}
                href={inErrorEngineeringHref}
              />
            </Box>
          </Box>

          <Divider 
            orientation="vertical" 
            flexItem 
            sx={{ 
              display: { xs: "none", lg: "flex" },
            }} 
          />

          {/* Workflow Status Section */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="caption"
              color="info.main"
              sx={{ 
                mb: 1.5, 
                display: "block", 
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                fontSize: "0.7rem",
              }}
            >
              Workflow Status
            </Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                gap: 1.5,
              }}
            >
              <KeyMetric
                name="Wait for All"
                value={data?.workflowsCountWaitForAll}
                loading={loading}
                href={waitForAllHref}
              />
              <KeyMetric
                name="Staff"
                value={data?.workflowsCountHuman}
                loading={loading}
                href={inManualHref}
              />
              <KeyMetric
                name="Staff Unassigned"
                value={data?.workflowsCountHumanUnassigned}
                loading={loading}
                href={inManualUnassignedHref}
              />
              <KeyMetric
                name="In Progress"
                value={data?.workflowsCountInProgress}
                loading={loading}
                href={inProgressHref}
              />
            </Box>
          </Box>

          <Divider 
            orientation="vertical" 
            flexItem 
            sx={{ 
              display: { xs: "none", lg: "flex" },
            }} 
          />

          {/* Automation & Geppetto Section */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="caption"
              color="success.main"
              sx={{ 
                mb: 1.5, 
                display: "block", 
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                fontSize: "0.7rem",
              }}
            >
              Automation & Geppetto
            </Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                gap: 1.5,
              }}
            >
              <KeyMetric
                name="In Automation"
                value={data?.workflowsCountAutomationInProgress}
                loading={loading}
                href={inAutomationHref}
              />
              <KeyMetric
                name="Automation Error"
                value={data?.workflowsCountAutomationError}
                loading={loading}
                href={automationInErrorHref}
              />
              <KeyMetric
                name="Geppetto In Flight"
                value={data?.workflowsCountGeppettoInFlight}
                loading={loading}
                href={geppettoInFlightHref}
              />
              <KeyMetric
                name="Geppetto Error"
                value={data?.workflowsCountGeppettoError}
                loading={loading}
                href={geppettoInErrorHref}
              />
            </Box>
          </Box>
        </Stack>
      </Stack>
    </Paper>
  );
}
